import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { API_ENDPOINT } from "../../constants/constants";
import { getToken } from "../../utils/util";
import BackBtn from "../widgets/BackBtn";

function UserDetails() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = () => {
    setLoading(true);
    axios
      .get(`${API_ENDPOINT}admin/get-user/${userId}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      })
      .then((response) => {
        setUser(response.data.user);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching user:", error);
        setLoading(false);
        showSnackbar("Error fetching user details", "error");
      });
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleEditLicenseClick = () => {
    setUpdateDialogOpen(true);
    setSelectedFile(null);
    setPreviewUrl("");
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        showSnackbar("Please select an image file", "error");
        return;
      }

      // Validate file size (e.g., 5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar("File size should be less than 5MB", "error");
        return;
      }

      setSelectedFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateLicense = () => {
    if (!selectedFile) {
      showSnackbar("Please select a file first", "error");
      return;
    }

    const formData = new FormData();
    formData.append("licence_certificate", selectedFile);

    setLoading(true);
    axios
      .put(`${API_ENDPOINT}admin/update-license/${userId}`, formData, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        setUser(response.data.user); // Update user data with response
        setUpdateDialogOpen(false);
        setSelectedFile(null);
        setPreviewUrl("");
        showSnackbar("License certificate updated successfully!");
      })
      .catch((error) => {
        console.error("Error updating license:", error);
        showSnackbar("Error updating license certificate", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCloseDialog = () => {
    setUpdateDialogOpen(false);
    setSelectedFile(null);
    setPreviewUrl("");
  };

  if (!user) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  const detailGroups = {
    personalDetails: {
      title: "Personal Details",
      details: [
        ["Language", user.lan],
        ["Name", user.name],
        ["User Type", user.user_type],
        ["Building", user.building],
        ["Area", user.area],
        ["Taluk", user.taluk],
        ["District", user.district],
        ["State", user.state],
        ["PIN", user.pin],
        ["Phone", user.phone],
        ["Landline", user.landline],
        ["Email", user.email],
      ],
    },
    idProofs: {
      title: "Uploaded ID proofs",
      details: [["Document Type", user.doc_type]],
      customDetail: (
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight="bold">
            ID Proof
          </Typography>
          <img
            src={user.doc_url}
            alt="Document"
            style={{
              maxHeight: "200px",
              maxWidth: "100%",
              border: "1px solid #ddd",
              borderRadius: "4px",
              marginBottom: "16px",
            }}
          />
          <Divider />
          <Box mt={2}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                CSB Licence certificate
              </Typography>
              <IconButton
                color="primary"
                onClick={handleEditLicenseClick}
                size="small"
              >
                <EditIcon />
              </IconButton>
            </Box>
            {user.licence_url ? (
              <img
                src={user.licence_url}
                alt="CSB License Certificate"
                style={{
                  maxHeight: "200px",
                  maxWidth: "100%",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            ) : (
              <Typography variant="body2" color="textSecondary">
                No license certificate uploaded
              </Typography>
            )}
          </Box>
        </Grid>
      ),
    },
    companySpecifics: {
      title: "Company Specifics",
      details: [
        ["Company", user.company],
        ["Licence Number", user.licence_number],
        ["GSTIN", user.gstin],
        ...(user.user_type !== "ssp"
          ? [["Mulberry Area(in acres)", user.mulberry_area]]
          : []),
        ...(user.user_type === "swr" || user.user_type === "ssp"
          ? []
          : [["Source of Eggs", user.egg_source]]),
        ...(user.user_type !== "asr"
          ? [
              [
                user.user_type === "swr"
                  ? "Cocoons yield per year"
                  : "DFLS per Year",
                user.dfls_per_year,
              ],
            ]
          : []),
        ...(user.user_type === "swr" ? [["CRC", user.crc]] : []),
        ...(user.user_type === "swr"
          ? [["Cocoons Market", user.cocoons_market]]
          : []),
        ["Year of establishment", user.yoe ?? ""],
        ["Seed Type", user.seed_type],
      ],
    },
    bankDetails: {
      title: "Bank Details",
      details: [
        ["Bank Name", user.bank?.name],
        ["Beneficiary", user.bank?.beneficiary],
        ["Account No.", user.bank?.account_no],
        ["IFSC", user.bank?.ifsc],
        ["Account Type", user.bank?.account_type],
      ],
    },
  };

  return (
    <Box p={3}>
      <BackBtn />
      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          User Details
        </Typography>

        <Avatar
          src={user.profile_pic}
          alt={`${user.name || "User"}'s profile`}
          sx={{ width: 120, height: 120, mb: 2 }}
        />

        <Typography fontSize={"20px"} mb={2} fontWeight={"bold"}>
          {user.name}
        </Typography>

        {Object.entries(detailGroups).map(([key, group]) => (
          <Accordion key={key}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{group.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {group.details.map(([label, value], index) => (
                  <React.Fragment key={`${key}-${index}`}>
                    <Grid item xs={4}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {label}
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body1">
                        {value || "Not provided"}
                      </Typography>
                    </Grid>
                  </React.Fragment>
                ))}
                {group.customDetail}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      {/* Update License Dialog */}
      <Dialog
        open={updateDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update CSB License Certificate</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="license-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="license-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                Select New License Certificate
              </Button>
            </label>

            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                Selected: {selectedFile.name}
              </Typography>
            )}

            {previewUrl && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Typography variant="subtitle2" gutterBottom>
                  Preview:
                </Typography>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    maxHeight: "300px",
                    maxWidth: "100%",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateLicense}
            variant="contained"
            disabled={!selectedFile || loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? "Updating..." : "Update License"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default UserDetails;
